import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { MentionTextarea } from '@/components/mentions/MentionTextarea';
import { cn } from '@/lib/utils';

interface FormTextareaFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder: string;
  description?: React.ReactNode;
  required?: boolean;
  maxLength?: number;
  className?: string;
  showCounter?: boolean;
  mentions?: boolean;
}

export const FormTextareaField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  required,
  maxLength,
  className,
  showCounter = true,
  mentions = false,
}: FormTextareaFieldProps<TFieldValues>) => {
  const { communityId } = useParams<{ communityId?: string }>();
  const mentionCommunityId = mentions ? communityId : undefined;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
          <FormControl>
            {mentionCommunityId ? (
              <MentionTextarea
                communityId={mentionCommunityId}
                value={typeof field.value === 'string' ? field.value : ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                className={className}
              />
            ) : (
              <Textarea
                placeholder={placeholder}
                className={cn('resize-none', className)}
                maxLength={maxLength}
                showCounter={showCounter}
                {...field}
              />
            )}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FormTextareaField.displayName = 'FormTextareaField';
